import React, { useMemo } from "react";

import { RangeIterator } from "../../utils";
import {
  GeneralInformation,
  LTDBenefits,
  LtdCoverage,
  LtdPlan,
  PlanRates,
} from "../../constants/aquafina";
import Rates from "../rates";
import ContributionType from "../sections/contribution-type-section";
import CollapsableSection from "../sections/collapsable-section";
import { CoverageRow, NotProvidedRow } from "../table/rows";
import { CoverageCell } from "../table/cells";
import { ViewMode } from "../../constants/types";
import { VIEW_MODES } from "../../constants";
import PolicyGeneralInfo from "../sections/policy-general-info";
import ProposalGeneralInfo from "../sections/proposal-general-info";

type Props = {
  viewMode: ViewMode;
  coverage: LtdCoverage;
  displayV4Fields: boolean;
};

const Ltd = ({ coverage, viewMode, displayV4Fields }: Props) => {
  const state = useMemo(() => {
    const benefitDetails: LTDBenefits[] = [];
    const generalInfo: GeneralInformation[] = [];
    const rates: PlanRates[] = [];
    const plans: LtdPlan[] = [];
    let inconsistentRateData = false;
    RangeIterator(coverage.plans.length, (index) => {
      const plan = coverage.plans[index];
      benefitDetails.push(plan.benefitDetails);
      generalInfo.push(plan.generalInformation);
      rates.push(plan.planRates);
      plans.push(plan);
      if (plan.inconsistentRateData === true) {
        inconsistentRateData = true;
      }
    });
    return { benefitDetails, generalInfo, rates, plans, inconsistentRateData };
  }, [coverage]);

  return (
    <>
      {viewMode === VIEW_MODES.POLICY ? (
        <>
          <PolicyGeneralInfo data={state.generalInfo} />
          <ContributionType plans={state.plans} />
        </>
      ) : (
        <ProposalGeneralInfo data={state.generalInfo} />
      )}
      <LTDBenefitDetails
        benefits={state.benefitDetails}
        displayV4Fields={displayV4Fields}
      />
      <Rates
        rates={state.rates}
        inconsistentRateData={state.inconsistentRateData}
        totalPlans={
          state.rates.length === 0 ? state.plans.length : state.rates.length
        }
      />
    </>
  );
};

type LTDBenefitDetailsProps = {
  benefits: LTDBenefits[];
  displayV4Fields: boolean;
};

const LTDBenefitDetails = ({
  benefits,
  displayV4Fields,
}: LTDBenefitDetailsProps) => (
  <CollapsableSection numberOfColumns={benefits.length} title="Benefit Details">
    <LTDBenefitDetailsData
      benefits={benefits}
      displayV4Fields={displayV4Fields}
    />
  </CollapsableSection>
);

const LTDBenefitDetailsData = ({
  benefits,
  displayV4Fields,
}: LTDBenefitDetailsProps) => {
  if (benefits.every((b) => b === null)) {
    return <NotProvidedRow colSpan={benefits.length} />;
  }

  return (
    <>
      <CoverageRow label="Benefit Amount" isWhite>
        {RangeIterator(benefits.length, (index) => (
          <CoverageCell
            value={benefits[index].benefitDescription}
            key={index}
          />
        ))}
      </CoverageRow>
      <CoverageRow label="Maximum Amount">
        {RangeIterator(benefits.length, (index) => (
          <CoverageCell value={benefits[index].maximumAmount} key={index} />
        ))}
      </CoverageRow>
      {displayV4Fields ? (
        <CoverageRow label="Guarantee Issue" isWhite>
          {RangeIterator(benefits.length, (index) => (
            <CoverageCell value={benefits[index].guaranteeIssue} key={index} />
          ))}
        </CoverageRow>
      ) : null}
      <CoverageRow label="Definition of Disability" isWhite={!displayV4Fields}>
        {RangeIterator(benefits.length, (index) => (
          <CoverageCell
            value={benefits[index].definitionOfDisability}
            key={index}
          />
        ))}
      </CoverageRow>
      {displayV4Fields ? (
        <>
          <CoverageRow
            label="Gainful Earnings Test - Own Occupation"
            isWhite={displayV4Fields}
          >
            {RangeIterator(benefits.length, (index) => (
              <CoverageCell
                value={benefits[index].gainfulEarningsTestOwnOccupation}
                key={index}
              />
            ))}
          </CoverageRow>
          <CoverageRow
            label="Gainful Earnings Test - Any Occupation"
            isWhite={!displayV4Fields}
          >
            {RangeIterator(benefits.length, (index) => (
              <CoverageCell
                value={benefits[index].gainfulEarningsTestAnyOccupation}
                key={index}
              />
            ))}
          </CoverageRow>
        </>
      ) : null}
      <CoverageRow label="Elimination Period" isWhite={displayV4Fields}>
        {RangeIterator(benefits.length, (index) => (
          <CoverageCell value={benefits[index].eliminationPeriod} key={index} />
        ))}
      </CoverageRow>
      <CoverageRow label="Benefit Duration" isWhite={!displayV4Fields}>
        {RangeIterator(benefits.length, (index) => (
          <CoverageCell value={benefits[index].benefitDuration} key={index} />
        ))}
      </CoverageRow>
      {displayV4Fields ? (
        <CoverageRow label="Definition of Earnings" isWhite={displayV4Fields}>
          {RangeIterator(benefits.length, (index) => (
            <CoverageCell
              value={benefits[index].definitionOfEarnings}
              key={index}
            />
          ))}
        </CoverageRow>
      ) : null}
      <CoverageRow
        label="Special Conditions Limitations"
        isWhite={displayV4Fields}
      >
        {RangeIterator(benefits.length, (index) => (
          <CoverageCell
            value={benefits[index].specialConditionsLimitations}
            key={index}
          />
        ))}
      </CoverageRow>
      <CoverageRow label="Mental Illness" isWhite={!displayV4Fields}>
        {RangeIterator(benefits.length, (index) => (
          <CoverageCell value={benefits[index].mentalIllness} key={index} />
        ))}
      </CoverageRow>
      <CoverageRow label="Substance Abuse" isWhite={displayV4Fields}>
        {RangeIterator(benefits.length, (index) => (
          <CoverageCell value={benefits[index].substanceAbuse} key={index} />
        ))}
      </CoverageRow>
      <CoverageRow label="Pre-existing Condition" isWhite={!displayV4Fields}>
        {RangeIterator(benefits.length, (index) => (
          <CoverageCell
            value={benefits[index].preExistingCondition}
            key={index}
          />
        ))}
      </CoverageRow>
      <CoverageRow label="Rehab" isWhite={displayV4Fields}>
        {RangeIterator(benefits.length, (index) => (
          <CoverageCell value={benefits[index].rehab} key={index} />
        ))}
      </CoverageRow>
      {displayV4Fields ? (
        <>
          <CoverageRow label="COLA Included?" isWhite={!displayV4Fields}>
            {RangeIterator(benefits.length, (index) => (
              <CoverageCell
                value={benefits[index].isColaIncluded}
                key={index}
              />
            ))}
          </CoverageRow>
          <CoverageRow label="Conversion" isWhite={displayV4Fields}>
            {RangeIterator(benefits.length, (index) => (
              <CoverageCell
                value={benefits[index].isConversionIncluded}
                key={index}
              />
            ))}
          </CoverageRow>
          <CoverageRow label="Waiver of Premium" isWhite={!displayV4Fields}>
            {RangeIterator(benefits.length, (index) => (
              <CoverageCell
                value={benefits[index].isWaiverOfPremiumIncluded}
                key={index}
              />
            ))}
          </CoverageRow>
        </>
      ) : null}
    </>
  );
};

export default Ltd;
